import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, assetUrl } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLocale();
  const [caddies, setCaddies] = useState([]);

  useEffect(() => {
    api.caddies.list({ page: 1 }).then((d) => setCaddies(d.caddies ?? [])).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-brand-700 text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1200&q=80')" }}
        />
        <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
          <div className="text-6xl mb-4">⛳</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 leading-tight">
            Mahanakhon Golf
          </h1>
          <p className="text-lg sm:text-xl text-brand-100 max-w-2xl mx-auto mb-8">
            {t('hero')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {user ? (
              <Link to="/feed" className="btn-primary bg-white text-brand-700 hover:bg-brand-50 text-base px-8 py-3">
                {t('goToFeed')}
              </Link>
            ) : (
              <>
                <Link to="/signup" className="btn-primary bg-white text-brand-700 hover:bg-brand-50 text-base px-8 py-3">
                  {t('getStarted')}
                </Link>
                <Link to="/signin" className="btn-secondary border-white/30 text-white hover:bg-white/10 text-base px-8 py-3">
                  {t('signIn')}
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-10">{t('why')}</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: '📸', title: 'Caddie Profiles', desc: 'Browse authentic photos and bios from caddies across Bangkok\'s top golf courses.' },
            { icon: '📅', title: 'Availability Calendar', desc: 'Check when your favourite caddie is free before you even arrive at the course.' },
            { icon: '💬', title: 'Direct Messaging', desc: 'Reach out to any caddie directly — arrange details privately and conveniently.' },
          ].map((f) => (
            <div key={f.title} className="card p-6 text-center">
              <div className="text-4xl mb-3">{f.icon}</div>
              <h3 className="font-semibold text-gray-800 mb-2">{f.title}</h3>
              <p className="text-sm text-gray-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured caddies */}
      {caddies.length > 0 && (
        <section className="bg-brand-50 py-16">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-8">{t('meetCaddies')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {caddies.slice(0, 8).map((c) => (
                <Link key={c.id} to={`/caddies/${c.id}`} className="card p-4 text-center hover:shadow-md transition-shadow">
                  {c.avatar_url ? (
                    <img src={assetUrl(c.avatar_url)} alt={c.display_name} className="w-14 h-14 rounded-full object-cover mx-auto mb-2" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-xl font-bold mx-auto mb-2">
                      {c.display_name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <p className="font-medium text-sm text-gray-800 truncate">{c.display_name}</p>
                  {c.workplace && <p className="text-xs text-gray-400 truncate">{c.workplace}</p>}
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/signup" className="btn-primary">
                {t('seeAllCaddies')} →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="text-center py-8 text-sm text-gray-400 border-t border-gray-100">
        © {new Date().getFullYear()} Mahanakhon Golf · mahanakhon.golf
      </footer>
    </div>
  );
}
